import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { isArray } from 'lodash';

import { IELAsyncAutocompleteElement, IRequestEvent, SELECTION_MODE } from '..';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'el-async-autocomplete',
  templateUrl: './el-async-autocomplete.component.html',
  styleUrls: ['./el-async-autocomplete.component.scss'],
})
export class ElAsyncAutoCompleteComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  SELECTION_MODE = SELECTION_MODE;

  /** Overlay element to block page interactions when autocomplete is open */
  private scrollBlockOverlay: HTMLElement | null = null;

  @Input() label: string;
  @Input() filteredOptionsFromParent: IELAsyncAutocompleteElement[] = [];
  @Input() selectedForEdit:
    | IELAsyncAutocompleteElement
    | IELAsyncAutocompleteElement[];
  @Input() selectedText: string | string[];
  @Input() placeHolder = '';
  @Input() isMultiSelectEnabled = false;
  @Input() selectionModes: SELECTION_MODE = SELECTION_MODE.PLAIN;
  @Input() optionButtonLabel: string;
  @Input() isFreeTextEnabled = false;
  @Input() hasAvatar = false;
  @Input() disabled = false;
  @Input() isRequired = false;
  @Input() customError: string;
  @Input() hideChips = false;
  @Input() markAsTouched = false;
  @Input() showError = false;

  @Output() selectedItems = new EventEmitter<
    IELAsyncAutocompleteElement[] | IELAsyncAutocompleteElement
  >();
  @Output() asyncInput = new EventEmitter<IRequestEvent>();
  @Output() optionButton = new EventEmitter<
    IELAsyncAutocompleteElement | IELAsyncAutocompleteElement[]
  >();
  @Output() selectedFreeText = new EventEmitter<IELAsyncAutocompleteElement>();

  isLoading = false;
  isScrollLoading = false;

  frmCtrlFilterInput = new FormControl();

  filteredOptions: IELAsyncAutocompleteElement[] = [];
  selectedChipList: IELAsyncAutocompleteElement[] = [];

  @ViewChild('autocompleteInput')
  autocompleteInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  autoCompleteTrigger: MatAutocompleteTrigger;

  defaultAvatar = 'https://www.gravatar.com/avatar/';

  start = 0;
  @Input() size = 10;
  @Input() total = 0;

  ngOnInit(): void {
    console.log('selectedForEdit', this.selectedForEdit);

    if (this.isRequired) {
      this.frmCtrlFilterInput.setValidators(Validators.required);
      this.frmCtrlFilterInput.updateValueAndValidity();
    }

    window.addEventListener('scroll', this.onScrollEvent.bind(this), true);
    this.initializePreSelectedOptions();
  }

  ngAfterViewInit(): void {
    if (this.matAutocomplete) {
      this.matAutocomplete.opened.subscribe(() => {
        this.showScrollBlockOverlay();
      });

      this.matAutocomplete.closed.subscribe(() => {
        this.hideScrollBlockOverlay();
      });
    }
  }

  private showScrollBlockOverlay(): void {
    if (this.scrollBlockOverlay) return;

    this.scrollBlockOverlay = document.createElement('div');
    this.scrollBlockOverlay.className = 'el-autocomplete-scroll-block-overlay';
    this.scrollBlockOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: transparent;
      z-index: 999;
      pointer-events: auto;
    `;
    document.body.appendChild(this.scrollBlockOverlay);
  }

  private hideScrollBlockOverlay(): void {
    if (this.scrollBlockOverlay && this.scrollBlockOverlay.parentNode) {
      this.scrollBlockOverlay.parentNode.removeChild(this.scrollBlockOverlay);
      this.scrollBlockOverlay = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markAsTouched']?.currentValue) {
      this.frmCtrlFilterInput.markAsTouched();
    }
    if (changes['showError']?.currentValue === true) {
      this.frmCtrlFilterInput.setValue('');
      this.frmCtrlFilterInput.setValidators(Validators.required);
      this.frmCtrlFilterInput.updateValueAndValidity;
    }
    if (changes['showError']?.currentValue === false) {
      this.frmCtrlFilterInput.setValue('');
      this.frmCtrlFilterInput.clearValidators();
      this.frmCtrlFilterInput.updateValueAndValidity;
    }
    if (
      changes['isRequired']?.currentValue === true &&
      this.isMultiSelectEnabled &&
      this.selectedChipList.length === 0
    ) {
      this.frmCtrlFilterInput.setValue('');
      this.frmCtrlFilterInput.setValidators(Validators.required);
      this.frmCtrlFilterInput.updateValueAndValidity;
    }
    if (
      changes['isRequired']?.currentValue === false &&
      this.isMultiSelectEnabled
    ) {
      this.frmCtrlFilterInput.setValue('');
      this.frmCtrlFilterInput.clearValidators();
      this.frmCtrlFilterInput.updateValueAndValidity;
    }
    if (!this.frmCtrlFilterInput.value) {
      this.filteredOptionsFromParent = [];
      this.filteredOptions = [];
      this.start = 0;
      this.size = this.size ? this.size : 10;
    }

    const allOptionsChanges = changes['filteredOptionsFromParent'];
    if (
      allOptionsChanges &&
      allOptionsChanges.currentValue !== allOptionsChanges.previousValue
    ) {
      this.initializeFilteredArray();
    }

    const selectedOptionsChanges = changes['selectedForEdit'];
    if (selectedOptionsChanges) {
      const { currentValue, previousValue } = selectedOptionsChanges;

      if (currentValue !== previousValue) {
        console.log('Is run');
        this.initializePreSelectedOptions();
      }
    }
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScrollEvent, true);
    // Clean up overlay if component is destroyed while autocomplete is open
    this.hideScrollBlockOverlay();
  }

  private onScrollEvent(event: Event) {
    const element = event.target as HTMLElement;
    const atBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;
    if (atBottom) {
      const requestEvent: IRequestEvent = {
        type: 'scroll',
        event: this.frmCtrlFilterInput.value,
        start: this.filteredOptions.length,
        size: this.size,
      };

      if (this.total > this.filteredOptions.length) {
        this.isScrollLoading = true;
        this.asyncInput.emit(requestEvent);
      }
    }
  }

  initializePreSelectedOptions() {
    if (
      this.isMultiSelectEnabled &&
      isArray(this.selectedForEdit) &&
      this.selectedForEdit?.length
    ) {
      this.selectedForEdit.forEach((selectedOption) => {
        if (!this.selectedChipList.includes(selectedOption)) {
          this.selectedChipList.push(selectedOption);
          if (!this.hideChips)
            this.frmCtrlFilterInput.setValue(selectedOption.value);
        }
      });
    } else if (
      !this.isMultiSelectEnabled &&
      !isArray(this.selectedForEdit) &&
      this.selectedForEdit
    ) {
      if (!this.hideChips)
        this.frmCtrlFilterInput.setValue(this.selectedForEdit.value);
      if (
        this.SELECTION_MODE &&
        !this.selectedChipList.find(
          (e) =>
            e.key === (this.selectedForEdit as IELAsyncAutocompleteElement)?.key
        )
      ) {
        this.selectedChipList.push(this.selectedForEdit);
      }
    }

    // if (!this.hideChips) this.selectedChipList = [];
  }

  private initializeFilteredArray(): void {
    this.filteredOptions = this.filteredOptionsFromParent;
    this.isScrollLoading = false;
    this.isLoading = false;
  }

  /**
   * This function can be used to remove the selected chips from  `selectedChipList`
   * @param selectChip {IELAsyncAutocompleteElement} accepts IELAsyncAutocompleteElement element instance
   * @returns {void} a promise object
   * @example
   *  selectedArray.splice(index, 1);
   */
  async removeChip(selectChip: IELAsyncAutocompleteElement): Promise<void> {
    const index = this.selectedChipList.indexOf(selectChip);

    if (index >= 0) {
      this.selectedChipList.splice(index, 1);
      this.autocompleteInput.nativeElement.readOnly = false;
      if (!this.optionButtonLabel) {
        this.selectedItems.emit(this.selectedChipList);
      }
    }
    if (!this.frmCtrlFilterInput.value || this.selectedChipList.length === 0) {
      this.filteredOptionsFromParent = [];
      this.filteredOptions = [];
    }
    if (
      this.selectionModes === SELECTION_MODE.CHIPS &&
      !this.hideChips &&
      this.selectedChipList.length === 0 &&
      ((this.isRequired && this.isMultiSelectEnabled) ||
        !this.isMultiSelectEnabled)
    ) {
      this.frmCtrlFilterInput.setValue('');
      if (this.isRequired)
        this.frmCtrlFilterInput.setValidators(Validators.required);
      this.frmCtrlFilterInput.updateValueAndValidity();
    }
  }

  onSelect(option: IELAsyncAutocompleteElement) {
    if (this.hideChips) this.selectedChipList = [];

    if (!this.isMultiSelectEnabled) {
      this.frmCtrlFilterInput.setValue(option.value);
      if (!this.optionButtonLabel) {
        this.selectedItems.emit(option);
      }
      const requestEvent: IRequestEvent = {
        type: 'typing',
        event: this.frmCtrlFilterInput.value,
        start: this.start,
        size: this.size,
      };
      if (this.total > this.filteredOptionsFromParent.length) {
        this.asyncInput.emit(requestEvent);
      }

      if (this.selectionModes === SELECTION_MODE.CHIPS) {
        if (this.selectedChipList.length === 0) {
          this.selectedChipList.push(option);
          if (
            this.selectionModes === SELECTION_MODE.CHIPS &&
            !this.hideChips &&
            this.selectedChipList.length > 0
          ) {
            this.frmCtrlFilterInput.clearValidators();
            this.frmCtrlFilterInput.updateValueAndValidity();
          }
          this.autocompleteInput.nativeElement.readOnly = true;
        }
        this.autocompleteInput.nativeElement.value = '';
        this.frmCtrlFilterInput.setValue(null);
      }
      return;
    } else {
      this.frmCtrlFilterInput.clearValidators();
      this.frmCtrlFilterInput.updateValueAndValidity();
    }
    if (
      this.filteredOptions.length > 0 &&
      !this.selectedChipList.some((item) => item.key === option.key)
    ) {
      this.selectedChipList.push(option);
      if (!this.optionButtonLabel) {
        this.selectedItems.emit(this.selectedChipList);
      }
    }
    this.autocompleteInput.nativeElement.value = '';
    this.frmCtrlFilterInput.setValue(null);
  }

  onOptionButtonClicked() {
    if (
      this.selectionModes === SELECTION_MODE.PLAIN &&
      this.frmCtrlFilterInput.value
    ) {
      this.optionButton.emit(this.frmCtrlFilterInput.value);
    } else if (
      this.selectionModes === SELECTION_MODE.CHIPS &&
      !this.frmCtrlFilterInput.value &&
      this.selectedChipList.length > 0
    ) {
      this.optionButton.emit(this.selectedChipList);
    }
  }

  OnFilterTextInputs(value: string) {
    if (value !== '') {
      this.isLoading = true;
      const requestEvent: IRequestEvent = {
        type: 'typing',
        event: this.frmCtrlFilterInput.value,
        start: this.start,
        size: this.size,
      };
      this.asyncInput.emit(requestEvent);
    } else {
      this.filteredOptionsFromParent = [];
      this.filteredOptions = [];
    }
  }

  getPlaceHolder(): string {
    if (
      this.selectionModes === SELECTION_MODE.CHIPS &&
      !this.isMultiSelectEnabled
    ) {
      if (this.selectedChipList.length === 0) {
        return this.placeHolder;
      }
      return '';
    }
    return this.placeHolder;
  }

  onAddFreeTextAsChip(event: MatChipInputEvent): void {
    if (!this.isMultiSelectEnabled) this.selectedChipList = [];
    const value = (event.value || '').trim();
    if (
      value &&
      this.filteredOptionsFromParent.length === 0 &&
      this.isFreeTextEnabled
    ) {
      const newFreeText = {
        key: '',
        value,
        avatar: this.defaultAvatar,
      };
      this.selectedChipList.push(newFreeText); //TODO:nipun add the proper key
      if (
        !this.isMultiSelectEnabled &&
        this.selectionModes === SELECTION_MODE.CHIPS
      ) {
        this.autocompleteInput.nativeElement.readOnly = true;
      } else if (this.isMultiSelectEnabled) {
        this.filteredOptions.push(newFreeText); //TODO:nipun
      }
      this.autocompleteInput.nativeElement.value = '';
    }
  }

  onSelectFreeText() {
    if (
      this.frmCtrlFilterInput.value &&
      this.filteredOptionsFromParent.length === 0 &&
      this.isFreeTextEnabled
    ) {
      if (
        !this.isMultiSelectEnabled &&
        this.selectionModes === SELECTION_MODE.CHIPS
      )
        this.autocompleteInput.nativeElement.readOnly = true;

      this.selectedFreeText.emit(this.frmCtrlFilterInput.value);
    }
  }

  focusChanged() {
    if (this.isFreeTextEnabled)
      this.autocompleteInput.nativeElement.readOnly = false;
  }

  onSelectAll() {
    // if (!this.hideChips) this.selectedChipList = [];
    this.filteredOptions.forEach((option: IELAsyncAutocompleteElement) => {
      if (
        this.filteredOptions.length > 0 &&
        !this.selectedChipList.some((item) => item.key === option.key)
      )
        this.selectedChipList.push(option);
    });
    if (
      this.selectionModes === SELECTION_MODE.CHIPS &&
      !this.hideChips &&
      this.selectedChipList.length > 0
    ) {
      this.frmCtrlFilterInput.clearValidators();
      this.frmCtrlFilterInput.updateValueAndValidity();
    }
    if (!this.optionButtonLabel) this.selectedItems.emit(this.selectedChipList);

    this.autocompleteInput.nativeElement.value = '';
    this.frmCtrlFilterInput.setValue(null);
  }

  isOptionDisabled(option: IELAsyncAutocompleteElement): boolean {
    if (
      this.isMultiSelectEnabled &&
      this.selectionModes === SELECTION_MODE.CHIPS
    )
      return this.selectedChipList.some((item) => item.key === option.key);

    return false;
  }

  isButtonDisabled(): boolean {
    return this.selectedChipList.length === 0;
  }
}
